import { useState, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { User, Briefcase, FileText, Home, ShieldCheck } from "lucide-react";
// ADV-1171: /warranties/:id is the OBLIGATION detail (ADR-0047). Coverage =
// effective_start_date → expiration_date (DB-computed). Production dates live
// only on warranty CLAIMS (the remediation work) and never appear here.
import { useWarranty, useWarrantyClaims } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import { StatusBadge } from "@/components/status-badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DataTable } from "@/components/v2/data-table";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function LinkedChip({
  icon,
  label,
  sublabel,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  sublabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-left hover:bg-[color:var(--color-elevated)] w-full sm:w-1/2"
    >
      <span className="shrink-0 text-[color:var(--color-text-md)]">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block truncate typo-body font-medium text-[color:var(--color-text-hi)]">
          {label}
        </span>
        <span className="block typo-label text-[color:var(--color-text-md)]">
          {sublabel}
        </span>
      </span>
    </button>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function pct(value: number | null): string | null {
  return value == null ? null : `${value}%`;
}

const claimColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "display_id",
    header: "Claim",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="typo-mono font-medium text-[color:var(--color-text-hi)]">
        {(row.original.display_id as string | undefined) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "warranty_status",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.warranty_status as string | undefined;
      return value ? (
        <StatusBadge status={value} />
      ) : (
        <span className="text-[color:var(--color-text-lo)]">—</span>
      );
    },
  },
  {
    accessorKey: "project_type",
    header: "Project",
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.project_type as string | undefined;
      return (
        <span className={value ? "" : "text-[color:var(--color-text-lo)]"}>
          {value || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "production_start",
    header: "Production",
    enableSorting: false,
    cell: ({ row }) => {
      const start = row.original.production_start as string | undefined;
      const end = row.original.production_end as string | undefined;
      if (!start && !end) {
        return <span className="text-[color:var(--color-text-lo)]">—</span>;
      }
      return (
        <span className="text-[color:var(--color-text-md)]">
          {start ? formatDate(start) : "—"} – {end ? formatDate(end) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "urgency",
    header: "Urgency",
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.urgency as string | undefined;
      return (
        <span className={value ? "" : "text-[color:var(--color-text-lo)]"}>
          {value ? titleCase(value) : "—"}
        </span>
      );
    },
  },
];

// Lazily-mounted Claims tab — the warranty-claims query only runs once the
// user opens the tab, because Radix Tabs unmounts inactive TabsContent by
// default. Lifting the hook call here keeps it out of the Overview/Activity
// render paths entirely.
function ClaimsTab({
  warrantyId,
  onClaimClick,
}: {
  warrantyId: string;
  onClaimClick: (id: string) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading } = useWarrantyClaims({
    warranty_id: warrantyId,
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });
  const claims = (data?.warranty_claims ?? []) as Row[];
  const total = data?.total ?? claims.length;

  return (
    <DataTable
      columns={claimColumns}
      data={claims}
      total={total}
      isLoading={isLoading}
      onRowClick={(row) => onClaimClick(row.id as string)}
      emptyMessage="No claims filed against this warranty obligation"
      emptyDescription="Claims appear here when a customer reports an issue covered by this warranty."
      pageSize={pageSize}
      pageIndex={pageIndex}
      onPaginationChange={setPageIndex}
      sorting={sorting}
      onSortingChange={setSorting}
    />
  );
}

export default function V2WarrantyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const { data, isLoading, isError } = useWarranty(id ?? "");
  const warranty = (data as { warranty?: Row } | undefined)?.warranty;

  const displayId = str(warranty?.display_id);
  const customerName = str(warranty?.customer_name);
  const customerContactId = str(warranty?.customer_contact_id);
  const termName = str(warranty?.term_name);
  const termCategory = str(warranty?.term_category);
  const status = str(warranty?.status);
  const effectiveStart = str(warranty?.effective_start_date);
  const expiration = str(warranty?.expiration_date);
  const transferable = bool(warranty?.is_transferable);
  const laborPct = num(warranty?.labor_covered_pct);
  const partsPct = num(warranty?.parts_covered_pct);
  const expensesPct = num(warranty?.expenses_covered_pct);
  const deductible = num(warranty?.deductible_amount);
  const registrationNumber = str(warranty?.registration_number);
  const registeredAt = str(warranty?.registered_at);
  // ADV-1207 / ADR-0051 §1: provider re-pointed to the trade-partner contact role.
  const providerAccountId = str(warranty?.provider_contact_role_id);
  const jobId = str(warranty?.job_id);
  const opportunityId = str(warranty?.opportunity_id);
  const propertyId = str(warranty?.property_id);
  const voidedReason = str(warranty?.voided_reason);
  const transferredAt = str(warranty?.transferred_at);

  const title = displayId ? `Warranty ${displayId}` : "Warranty";

  const coverageWindow =
    effectiveStart || expiration
      ? `${effectiveStart ? formatDate(effectiveStart) : "—"} – ${
          expiration ? formatDate(expiration) : "—"
        }`
      : "—";

  const transferableLabel =
    transferable === true ? "Yes" : transferable === false ? "No" : "—";

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !warranty}
      title={title}
      subtitle={customerName ?? undefined}
      breadcrumbs={[
        { label: "Warranties", href: "/warranties" },
        { label: title },
      ]}
      notFoundMessage={{
        title: "Warranty not found",
        description: "This warranty obligation may have been deleted.",
      }}
      actions={<DetailPageActions entityLabel="Warranty" />}
    >
      <Tabs
        defaultValue="overview"
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col"
      >
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0 mb-0">
          <TabsTrigger
            value="overview"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="claims"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Claims
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Activity
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-0 pt-6">
          <div className="flex gap-6">
            <div className="flex-1 min-w-0 space-y-5">
              {/* Inline properties strip */}
              <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
                {status && <StatusBadge status={status} />}
                {termName && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>{termName}</span>
                  </>
                )}
                {warranty?.created_at != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>
                      Created {formatDate(warranty.created_at as string)}
                    </span>
                  </>
                )}
              </div>

              {/* KPI row — Coverage Window is sourced from effective_start_date
                  / expiration_date (the obligation's REAL coverage, never
                  production dates per ADR-0047). */}
              <KPIRow
                cards={[
                  {
                    label: "Status",
                    value: status ? titleCase(status) : "—",
                  },
                  {
                    label: "Term",
                    value: termName ?? "—",
                  },
                  {
                    label: "Coverage Window",
                    value: coverageWindow,
                  },
                  {
                    label: "Transferable",
                    value: transferableLabel,
                  },
                ]}
              />

              {/* Coverage */}
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
                <div className="px-4 pt-3">
                  <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                    Coverage
                  </span>
                </div>
                <div className="px-4 py-2">
                  <InfoRow
                    label="Effective Start"
                    value={effectiveStart ? formatDate(effectiveStart) : null}
                  />
                  <InfoRow
                    label="Expiration"
                    value={expiration ? formatDate(expiration) : null}
                  />
                  <InfoRow
                    label="Status"
                    value={status ? titleCase(status) : null}
                  />
                  <InfoRow label="Transferable" value={transferableLabel} />
                  <InfoRow label="Labor Covered" value={pct(laborPct)} />
                  <InfoRow label="Parts Covered" value={pct(partsPct)} />
                  <InfoRow label="Expenses Covered" value={pct(expensesPct)} />
                  <InfoRow
                    label="Deductible"
                    value={
                      deductible != null ? formatCurrency(deductible) : null
                    }
                  />
                  {voidedReason && (
                    <InfoRow label="Voided Reason" value={voidedReason} />
                  )}
                  {transferredAt && (
                    <InfoRow
                      label="Transferred"
                      value={formatDate(transferredAt)}
                    />
                  )}
                  {!effectiveStart &&
                    !expiration &&
                    laborPct == null &&
                    partsPct == null && (
                      <p className="py-1 typo-body text-[color:var(--color-text-lo)]">
                        No coverage details recorded.
                      </p>
                    )}
                </div>
              </div>

              {/* Provider / registration (manufacturer + extended only) */}
              {(providerAccountId || registrationNumber) && (
                <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
                  <div className="px-4 pt-3">
                    <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                      Provider
                    </span>
                  </div>
                  <div className="px-4 py-2">
                    <InfoRow
                      label="Registration Number"
                      value={registrationNumber}
                    />
                    <InfoRow
                      label="Registered"
                      value={registeredAt ? formatDate(registeredAt) : null}
                    />
                    <InfoRow
                      label="Provider Account"
                      value={providerAccountId}
                    />
                  </div>
                </div>
              )}

              {/* Linked records — term is shown as a non-link chip (no term
                  detail page yet); job / opportunity / property / customer
                  navigate to their existing pages. */}
              <div className="flex flex-wrap gap-2">
                {(termName || termCategory) && (
                  <span className="flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 w-full sm:w-1/2">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-[color:var(--color-text-md)]" />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate typo-body font-medium text-[color:var(--color-text-hi)]">
                        {termName ?? "Untitled term"}
                      </span>
                      <span className="block typo-label text-[color:var(--color-text-md)]">
                        {termCategory
                          ? `${titleCase(termCategory)} term`
                          : "Warranty Term"}
                      </span>
                    </span>
                  </span>
                )}
                {jobId && (
                  <LinkedChip
                    icon={<Briefcase className="h-4 w-4" />}
                    label="View job"
                    sublabel="Originating Job"
                    onClick={() => navigate(`/jobs/${jobId}`)}
                  />
                )}
                {opportunityId && (
                  <LinkedChip
                    icon={<FileText className="h-4 w-4" />}
                    label="View opportunity"
                    sublabel="Linked Opportunity"
                    onClick={() => navigate(`/opportunities/${opportunityId}`)}
                  />
                )}
                {propertyId && (
                  <LinkedChip
                    icon={<Home className="h-4 w-4" />}
                    label="View property"
                    sublabel="Covered Property"
                    onClick={() => navigate(`/locations/${propertyId}`)}
                  />
                )}
                {customerName && customerContactId && (
                  <LinkedChip
                    icon={<User className="h-4 w-4" />}
                    label={customerName}
                    sublabel="Customer"
                    onClick={() => navigate(`/customers/${customerContactId}`)}
                  />
                )}
              </div>
            </div>

            {/* Right rail */}
            <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
              <RailSection title="Properties">
                <InfoRow
                  label="Status"
                  value={status ? titleCase(status) : null}
                />
                <InfoRow label="Customer" value={customerName} />
                <InfoRow label="Term" value={termName} />
                <InfoRow
                  label="Category"
                  value={termCategory ? titleCase(termCategory) : null}
                />
                <InfoRow
                  label="Effective"
                  value={effectiveStart ? formatDate(effectiveStart) : null}
                />
                <InfoRow
                  label="Expires"
                  value={expiration ? formatDate(expiration) : null}
                />
                <InfoRow label="Transferable" value={transferableLabel} />
                <InfoRow
                  label="Created"
                  value={
                    warranty?.created_at
                      ? formatDate(warranty.created_at as string)
                      : null
                  }
                />
              </RailSection>

              {/* Activity preview — compact 4-row rail */}
              {id && (
                <RailSection title="Activity">
                  <EntityActivityPreview entityType="warranty" entityId={id} />
                </RailSection>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── Claims ── (lazily mounted — see ClaimsTab above) */}
        <TabsContent value="claims" className="mt-0 pt-6">
          {id && (
            <ClaimsTab
              warrantyId={id}
              onClaimClick={(claimId) =>
                navigate(`/warranty-claims/${claimId}`)
              }
            />
          )}
        </TabsContent>

        {/* ── Activity ── */}
        <TabsContent value="activity" className="mt-0 pt-6">
          {id && (
            <EntityActivityFeed
              entityType="warranty"
              entityId={id}
              emptyDescription="Messages, calls, and notes on this warranty obligation appear here once the first contact is made."
            />
          )}
        </TabsContent>
      </Tabs>
    </DetailPage>
  );
}
